/**
 * <MutabaaVetoTestForm /> — one card per workbench test per §4.4.
 *
 * Renders the title + description, a collapsible "Preview payload"
 * block showing the deliberately-bad inputs, and a "Run sandbox test"
 * button that POSTs to the matching gov endpoint. Renders the result:
 *   - emerald-bordered success when the veto fires (response carries
 *     `error == 'mutabaa_veto'` with reason + article) — that's the
 *     demonstration HR shows the prospect
 *   - rose-bordered alert when the veto did NOT fire (anti-regression
 *     signal that the floor isn't being enforced)
 */

import { AlertTriangle, CheckCircle2, ChevronDown, Play } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Portal } from "./HealthCard";

export interface VetoTestConfig {
  /** Stable id — used for keys + storage. */
  id: string;
  /** i18n title key, e.g. "gov.mudad.test_article_88". */
  titleKey: string;
  /** i18n description key. */
  descKey: string;
  /** Backend endpoint to POST. */
  endpoint: string;
  /** Body shape that's deliberately-bad to fire the veto. */
  presetPayload: Record<string, unknown>;
  /** The article the veto should cite (display-only). */
  expectedVetoArticle: string;
}

export interface MutabaaVetoTestFormProps {
  portal: Portal;
  legalEntityId: string;
  test: VetoTestConfig;
}

type RunResult =
  | { kind: "veto"; article: string; reason: string }
  | { kind: "success"; data: unknown }
  | { kind: "error"; message: string };

export function MutabaaVetoTestForm({
  portal,
  legalEntityId,
  test,
}: MutabaaVetoTestFormProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  const run = useMutation({
    mutationFn: async (): Promise<RunResult> => {
      const url = `${test.endpoint}?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(test.presetPayload),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 422 && body?.error === "mutabaa_veto") {
        return {
          kind: "veto",
          article: body.article ?? test.expectedVetoArticle,
          reason: body.reason ?? "",
        };
      }
      if (!res.ok) {
        return {
          kind: "error",
          message:
            body?.detail ?? body?.reason ?? `HTTP ${res.status}`,
        };
      }
      return { kind: "success", data: body };
    },
    onSuccess: setResult,
    onError: (e: unknown) =>
      setResult({ kind: "error", message: String(e) }),
  });

  return (
    <article
      data-testid={`veto-test-${test.id}`}
      data-portal={portal}
      className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-4"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-medium text-text-primary">
            {t(test.titleKey, test.titleKey)}
          </h4>
          <p className="mt-1 text-xs text-text-tertiary leading-relaxed">
            {t(test.descKey, test.descKey)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-brand-blue/10 px-2 py-0.5 text-[10px] uppercase text-brand-deep">
          {test.expectedVetoArticle}
        </span>
      </header>

      <details
        open={open}
        onToggle={(e) =>
          setOpen((e.currentTarget as HTMLDetailsElement).open)
        }
        className="text-xs"
      >
        <summary className="inline-flex cursor-pointer items-center gap-1 text-text-tertiary hover:text-text-primary">
          <ChevronDown
            size={11}
            aria-hidden
            className={open ? "rotate-180 transition-transform" : "transition-transform"}
          />
          {t("gov.preview_payload", "Preview payload")}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded border border-border-subtle bg-surface-sunken p-2 font-mono text-[11px] text-text-secondary">
          {JSON.stringify(test.presetPayload, null, 2)}
        </pre>
      </details>

      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => {
            setResult(null);
            run.mutate();
          }}
          disabled={run.isPending}
          className="inline-flex items-center gap-1 rounded-sm bg-brand-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-deep disabled:opacity-50"
        >
          <Play size={11} aria-hidden />
          {run.isPending
            ? t("gov.running_sandbox_test", "Running…")
            : t("gov.run_sandbox_test", "Run sandbox test")}
        </button>
      </div>

      {result?.kind === "veto" && (
        <div
          data-testid={`veto-fired-${test.id}`}
          className="flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900"
        >
          <CheckCircle2
            size={14}
            aria-hidden
            className="mt-0.5 shrink-0 text-emerald-700"
          />
          <div>
            <p className="font-semibold">
              {t("gov.veto_fired_success", "Veto fired before submission")}
            </p>
            <p className="mt-1">
              <span className="font-medium">{result.article}</span>
              {result.reason ? ` — ${result.reason}` : ""}
            </p>
          </div>
        </div>
      )}
      {result?.kind === "success" && (
        <div
          data-testid={`veto-missed-${test.id}`}
          className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900"
        >
          <AlertTriangle
            size={14}
            aria-hidden
            className="mt-0.5 shrink-0 text-rose-700"
          />
          <div>
            <p className="font-semibold">
              {t(
                "gov.veto_did_not_fire_alert",
                "Test did NOT trigger expected veto — engineering should investigate",
              )}
            </p>
            <p className="mt-1 text-[11px]">
              {t("gov.expected_article", "Expected article")}:{" "}
              <span className="font-medium">{test.expectedVetoArticle}</span>
            </p>
          </div>
        </div>
      )}
      {result?.kind === "error" && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle
            size={14}
            aria-hidden
            className="mt-0.5 shrink-0 text-amber-700"
          />
          <div>
            <p className="font-semibold">
              {t("gov.test_runtime_error", "Test runtime error")}
            </p>
            <p className="mt-1 font-mono text-[10px]">{result.message}</p>
          </div>
        </div>
      )}
    </article>
  );
}

export default MutabaaVetoTestForm;
